import { anvilStrike } from './anvil.ts';
import { tollStrike } from './toll.ts';
import { wadeBp } from './drinker.ts';
import { tidePaceBp } from './tide.ts';
import { sunkenPaceBp } from './sunken.ts';
import { huntLeave, huntTrap } from './hunt.ts';
import { BLESSING, FORKS, HERO_DMG_TYPE, HEROES, LIVE_CHASE, NIGHTMARES, PURIFY_RANGE, PURIFY_TICKS, RELIC_AWAKENED_FX, RELIC_FX, SKILL_FX, SKILLS, WARRIOR } from '@somnhold/content';
import { awake, bigFoe, gearAfterHit, gearBlow, gearHeroTick, gearOnKill } from './gear.ts';
import { anselPage, kaelaSeed } from './relics.ts';
import { glassRoute } from './deep.ts';
import { shutRoomTarget } from './dungeon.ts';
import { carryBp, expCarry } from './expedition.ts';
import { plunderCarry, plunderCarryBp, plunderLeave } from './plunder.ts';
import { shining } from './obsidian.ts';
import { cellAtWorld, cellCenter, findPath, heroRoute, heroWalkable } from '../grid.ts';
import { hexToWorld } from '../hex.ts';
import { dist2, iatan2, idiv, imax, isqrt, mulBp, withinRange } from '../math/fixed.ts';
import { skillLevelBp } from './create.ts';
import { mazeClimb, mazeReach } from './maze.ts';
import { rngChance, rngInt } from '../math/rng.ts';
import type { BattleState, Command, Enemy, Hero } from '../types.ts';
import { damageEnemy, damageHero, enemiesNear, ev, findEnemy, gearOnHit, healHero, histAdd, isFlying, pstats, respawnHero, targetable, wake, wardHero } from './combat.ts';
import { stepToward } from './enemies.ts';
import { bloodlust, clawsAround, furyBp, heroBlowBp, heroHitExtras, isMelee, lifesteal, refreshStats, root, shiftBear } from './kits.ts';
import { castSkill, echoCast, overloadAt, whirlPulse } from './skills.ts';
import { launch } from './towers.ts';
import { lampHeal, uniqueHeroTick, watchShot } from './uniques.ts';
import { lucernaShot } from './forms.ts';
import { simaSpeedBp } from './sima.ts';
import { unsleepingHeroTick } from './unsleeping.ts';

/** How far an idle live hero will step out to auto-engage (melee). */
const MELEE_AUTO_CHASE = 1800;
/** After a move order lands, a live hero holds its spot this long (3 s) before stepping out to fight. */
const LIVE_CALM = 36;
/** Out-of-combat regen: after this many ticks without damage, 1 % max HP every 12 ticks. */
const REGEN_DELAY = 60;

export const applyCommand = (s: BattleState, h: Hero, c: Command): void => {
  if (c.t === 'control') {
    // A mercenary (or a bot-played hero) stays one: connections come and go around it.
    if (h.control !== 'merc') h.control = c.mode;
    return;
  }
  if (h.control === 'live') h.idleTicks = 0;
  if (h.downTicks > 0) return;
  switch (c.t) {
    case 'move': {
      // The Furrows: a tap into the dark walks as far as the light reaches that way.
      const { x: tx, y: ty } = moveTarget(s, h, c.x, c.y);
      // Walking away from a nearby fight counts as a retreat at the current HP.
      if (h.control === 'live') {
        const near = autoTarget(s, h, 2500);
        if (near && dist2(tx, ty, near.x, near.y) > dist2(h.x, h.y, near.x, near.y)) {
          histAdd(pstats(s, h.pid).retreatHist, idiv(h.hp * 100, h.stats.maxHp), 10);
        }
      }
      orderMove(s, h, tx, ty);
      h.target = 0;
      h.forced = false;
      break;
    }
    case 'attack': {
      const e = findEnemy(s, c.id);
      if (e && targetable(s, e)) {
        h.target = e.id;
        h.forced = true;
        h.moving = false;
        h.path = [];
      }
      break;
    }
    case 'stop':
      h.moving = false;
      h.path = [];
      h.target = 0;
      h.forced = false;
      h.ax = h.x;
      h.ay = h.y;
      break;
    case 'skill':
      castSkill(s, h, c);
      break;
    case 'turret':
      placeIncursionTurret(s, h, c.x, c.y);
      break;
    case 'overload':
      overloadAt(s, h, c.x, c.y);
      break;
    case 'carry':
      if (s.plunder) plunderCarry(s, h);
      else expCarry(s, h);
      break;
    case 'leave':
      if (s.rules?.hunt) huntLeave(s);
      else plunderLeave(s, h);
      break;
    case 'trap':
      huntTrap(s, h);
      break;
    case 'strike':
      // The hammer: on the Anvil (Las vías 3), or on the bell (El Párpado 4).
      if (s.rules?.toll) tollStrike(s, h);
      else anvilStrike(s, h);
      break;
    case 'climb':
      if (s.dungeon?.maze) mazeClimb(s, s.dungeon.maze, h);
      break;
    default:
      break;
  }
};

 /** Incursions: any class can drop up to 3 portable turrets that last the whole extraction. */
const placeIncursionTurret = (s: BattleState, h: Hero, x: number, y: number): void => {
  const rule = s.rules?.turrets;
  if (s.mode !== 'incursion' || (h.turretsPlaced ?? 0) >= (rule?.max ?? 3)) return;
  if (!withinRange(h.x, h.y, x, y, SKILLS.turret.castRange)) return;
  const cell = cellAtWorld(s, x, y);
  if (!heroWalkable(s, cell)) return;
  h.turretsPlaced = (h.turretsPlaced ?? 0) + 1;
  s.turrets.push({
    id: s.nextId++,
    owner: h.id,
    x,
    y,
    cd: 0,
    expires: s.tick + (rule?.ticks ?? 100000),
    // What a hero builds hits as hard as its gear says.
    damage: mulBp(rule?.damage ?? SKILLS.turret.damage, 10000 + (h.stats.damageBp ?? 0)),
    range: SKILLS.turret.radius,
    reload: SKILLS.turret.every,
    aim: 0,
    ...(rule ? { portable: true, hp: rule.hp, maxHp: rule.hp } : {}),
  });
  ev(s, { t: 'skill', hero: h.id, skill: 'turret', x, y });
};

/**
 * Where a move order takes a hero standing at `at` (sim units): the tap itself, in the Furrows a tap into the
 * dark only as far as the light reaches, in a crypt room with its doors shut no farther than its edge. The client's guess of my walk uses it too (game/predict.ts).
 */
export const moveTarget = (s: BattleState, at: { x: number; y: number }, x: number, y: number): { x: number; y: number } =>
  (s.dungeon?.maze ? mazeReach(s, s.dungeon.maze, at, x, y) : null) ?? shutRoomTarget(s, x, y) ?? { x, y };

/**
 * The walk to (x, y) from `at`: the cells to step through and where it ends. Shared with the client's guess, so
 * what is drawn ahead is the walk the room will make. Null: off the map.
 */
export const moveRoute = (s: BattleState, at: { x: number; y: number }, x: number, y: number): { path: number[]; x: number; y: number } | null => {
  const R = s.map.radius;
  const from = cellAtWorld(s, at.x, at.y);
  const to = cellAtWorld(s, x, y);
  if (to < 0) return null;
  const path = glassRoute(s, from, to) ?? heroRoute(s, from, to);
  // The Furrows: off the middle of its cell, a hero first steps back to it, so the way never cuts a wall's corner.
  if (s.dungeon?.maze && from >= 0 && path.length) {
    const mid = cellCenter(R, from);
    if (!withinRange(at.x, at.y, mid.x, mid.y, 150)) path.unshift(from);
  }
  // Never end inside a tower: drop trailing steps until the last one is a cell the hero can stand on.
  while (path.length > 0 && !heroWalkable(s, path[path.length - 1] as number)) path.pop();
  const last = path.length > 0 ? (path[path.length - 1] as number) : from;
  // Unreachable: stop at the center of the closest reachable cell.
  if (last !== to || !heroWalkable(s, to)) return { path, ...cellCenter(R, last) };
  return { path, x, y };
};

export const orderMove = (s: BattleState, h: Hero, x: number, y: number): void => {
  const r = moveRoute(s, h, x, y);
  if (!r) return;
  h.path = r.path;
  h.destX = r.x;
  h.destY = r.y;
  h.moving = true;
};

const heroPx = (h: Hero, s?: BattleState): number => {
  // A load slows its carrier (expeditions).
  const base = h.carry !== undefined && s ? mulBp(h.stats.speed, s.plunder ? plunderCarryBp(s, h) : carryBp(s, h)) : h.stats.speed;
  // Wading in the water that came back (El pozo 2).
  h.moveAcc += s?.drink?.front || s?.drink?.heart ? mulBp(base, wadeBp(s, h)) : s?.dungeon?.sima && h.carry === undefined ? mulBp(base, simaSpeedBp(s, h)) : s?.tide ? mulBp(base, tidePaceBp(s, h)) : s?.sunken?.heart ? mulBp(base, sunkenPaceBp(s, h)) : base;
  const px = idiv(h.moveAcc, 12);
  h.moveAcc -= px * 12;
  return px;
};

/** Walk along the cell path, then to the exact destination. Returns true on arrival. */
const followPath = (s: BattleState, h: Hero): boolean => {
  const R = s.map.radius;
  let px = heroPx(h, s);
  while (px > 0) {
    const nextCell = h.path[0];
    const tgt = nextCell !== undefined && h.path.length > 1 ? cellCenter(R, nextCell) : { x: h.destX, y: h.destY };
    const before = { x: h.x, y: h.y };
    const arrived = stepToward(h, tgt.x, tgt.y, px);
    const moved = Math.abs(h.x - before.x) + Math.abs(h.y - before.y);
    if (!arrived) return false;
    px -= moved > 0 ? moved : px;
    if (nextCell !== undefined && h.path.length > 1) h.path.shift();
    else {
      h.path = [];
      return true;
    }
  }
  return false;
};

const attackReady = (h: Hero): boolean => h.attackCd === 0;

/** Near a standing cleric (or one itself): the blessing. */
export const blessed = (s: BattleState, h: Hero): boolean =>
  s.heroes.some((o) => o.cls === 'cleric' && o.downTicks === 0 && withinRange(o.x, o.y, h.x, h.y, BLESSING.radius));

const basicAttack = (s: BattleState, h: Hero, e: Enemy): void => {
  h.facing = iatan2(e.y - h.y, e.x - h.x);
  if (!attackReady(h) || (h.fear ?? 0) > 0) return;
  const d = HEROES[h.cls];
  h.attackCd = h.stats.reload;
  h.anim = 'attack';
  h.animT = 0;
  let dmg = mulBp(mulBp(h.stats.attack, h.stats.powerBp), furyBp(h));
  // The blessing and a clan roar: the stronger of the two counts.
  const help = imax(blessed(s, h) ? BLESSING.damageBp : 10000, (h.roar ?? 0) > 0 ? SKILL_FX.clanRoar.damageBp : 10000);
  if (help > 10000) dmg = mulBp(dmg, help);
  // Fury and executioner (rare gear).
  dmg = gearBlow(h, e, dmg);
  // A hidden shot, a blessed weapon, a duel.
  dmg = mulBp(dmg, heroBlowBp(h, e));
  if (h.relics.includes('fateDie')) {
    // Fate die: every blow rolls it; a 6 (a 5 or a 6 awakened) lands twice as hard.
    const d = RELIC_FX.fateDie;
    const lucky = awake(h, 'fateDie') ? RELIC_AWAKENED_FX.fateDie.lucky : d.lucky;
    if (rngInt(s.rng, d.faces) >= d.faces - lucky) dmg *= 2;
  }
  // A critical: by chance, and always on a nightmare that can't fight back (docs/CRIATURAS.md §6).
  // The hawk eye: never a miss on an elite or a boss (on anything, keen).
  const eagle = (h.eagle ?? 0) > 0 && (h.keen || bigFoe(e));
  let crit = e.stun > 0 || eagle || (h.stats.critBp > 0 && rngChance(s.rng, h.stats.critBp));
  // The wanderer's lost trail (docs/UNICAS.md §3): the first shot after the leap is a critical.
  if (h.sureCrit) {
    crit = true;
    h.sureCrit = undefined;
  }
  if (crit) dmg = mulBp(dmg, h.stats.critMultBp);
  recordChoice(s, h, e);
  if (h.control === 'live') histAdd(pstats(s, h.pid).rangeHist, isqrt(dist2(h.x, h.y, e.x, e.y)), 500);
  if (d.projectileSpeed === 0 || h.stats.melee || (h.bear ?? 0) > 0) {
    const ht = (h.bear ?? 0) > 0 ? undefined : (h.blessed ?? 0) > 0 ? 'holy' : HERO_DMG_TYPE[h.cls];
    const dealt = damageEnemy(s, e, dmg, { pid: h.pid, ...(crit ? { crit } : {}), ...(ht ? { type: ht } : {}) });
    lifesteal(s, h, dealt);
    // Bloodletting, a choir, an atonement, a duel.
    heroHitExtras(s, h, e, dealt);
    gearOnHit(s, h, e, dealt);
    gearAfterHit(s, h, e, dealt);
    lampHeal(s, h, dealt);
    // The bear's claws land around the target too.
    if ((h.bear ?? 0) > 0) clawsAround(s, h, e, dmg, dealt);
    // The warrior's cleave: the blow carries on to those pressed around its target.
    if (h.cls === 'warrior' && WARRIOR.cleave.shareBp > 0) {
      const share = mulBp(dmg, WARRIOR.cleave.shareBp);
      const near = enemiesNear(s, e.x, e.y, WARRIOR.cleave.radius, (x) => x.id !== e.id && targetable(s, x));
      near.sort((a, b) => dist2(e.x, e.y, a.x, a.y) - dist2(e.x, e.y, b.x, b.y) || a.id - b.id);
      for (const o of near.slice(0, WARRIOR.cleave.targets)) damageEnemy(s, o, share, { pid: h.pid, type: HERO_DMG_TYPE.warrior });
    }
    // Ancestral fury, avatar of light: the blow lands around its target too.
    if ((h.areaBlow ?? 0) > 0 && h.areaBp) {
      const share = mulBp(dmg, h.areaBp);
      for (const o of enemiesNear(s, e.x, e.y, h.areaR ?? 0, (x) => x.id !== e.id && targetable(s, x))) lifesteal(s, h, damageEnemy(s, o, share, { pid: h.pid, ...(h.areaHoly ? { type: 'holy' as const } : {}) }));
      ev(s, { t: 'boom', x: e.x, y: e.y, r: h.areaR ?? 0, kind: h.areaHoly ? 'avatarBlow' : 'furyBlow' });
    }
    ev(s, { t: 'shot', from: h.id, to: e.id, kind: (h.bear ?? 0) > 0 ? 'claw' : 'slash', x: h.x, y: h.y, tx: e.x, ty: e.y });
    // Ansel's page: every few blows, fire around the target.
    anselPage(s, h, e, dmg);
    return;
  }
  anselPage(s, h, e, dmg);
  // The watchtower bow: every few arrows, one through the whole line.
  if (watchShot(s, h, e, dmg)) return;
  // The chapel lamp's lucerna: every few blows, a beam of light through the line (it still heals by the lamp).
  if (h.uqForm) {
    const lit = lucernaShot(s, h, e, dmg);
    if (lit >= 0) {
      lampHeal(s, h, lit);
      return;
    }
  }
  const kind = h.cls === 'mage' ? 'orb' : h.cls === 'artificer' ? 'bullet' : h.cls === 'cleric' ? 'light' : 'arrow';
  launch(s, {
    kind,
    x: h.x,
    y: h.y,
    tx: e.x,
    ty: e.y,
    target: e.id,
    speed: d.projectileSpeed,
    damage: dmg,
    splash: h.stats.splash,
    ignoresArmor: h.cls === 'mage' || h.cls === 'cleric',
    pierce: h.cls === 'ranger',
    enchant: null,
    stun: 0,
    pid: h.pid,
    ...(crit ? { crit } : {}),
    ...(h.stats.burnBp || h.stats.lifestealBp || h.stats.frostBp || h.stats.cleaveBp || h.stats.chainBp || h.stats.stunEvery || h.stats.pierceGear || h.uq?.length ? { hero: h.id } : {}),
    hostile: false,
  });
  ev(s, { t: 'shot', from: h.id, to: e.id, kind, x: h.x, y: h.y, tx: e.x, ty: e.y });
};

/** Ghost profile learning: which targeting rule would have picked this enemy. */
const recordChoice = (s: BattleState, h: Hero, e: Enemy): void => {
  if (h.control !== 'live') return;
  const near = enemiesNear(s, h.x, h.y, h.stats.range + 1500, (o) => targetable(s, o));
  if (near.length < 2) return;
  let nearest = near[0] as Enemy;
  let weakest = nearest;
  let strongest = nearest;
  let nd = 0x7fffffff;
  for (const o of near) {
    const d = dist2(h.x, h.y, o.x, o.y);
    if (d < nd) {
      nd = d;
      nearest = o;
    }
    if (o.hp < weakest.hp) weakest = o;
    if (o.maxHp > strongest.maxHp) strongest = o;
  }
  const c = pstats(s, h.pid).choice;
  if (e.id === nearest.id) c[0]++;
  if (e.id === weakest.id) c[1]++;
  if (e.id === strongest.id) c[2]++;
  if (e.tgtKind === 2) c[3]++;
};

/** The dragon of the Burn in its sky: no blade reaches it up there (docs/UNICAS.md §4.4). */
const overhead = (e: Enemy): boolean => e.kind === 'dragon' && !!e.air;

/** Best auto-target for a hero standing still. */
export const autoTarget = (s: BattleState, h: Hero, radius: number): Enemy | null => {
  let best: Enemy | null = null;
  let bd = 0x7fffffff;
  for (const e of enemiesNear(s, h.x, h.y, radius)) {
    if (!targetable(s, e)) continue;
    if (isMelee(h) && isFlying(e) && (overhead(e) || !withinRange(h.x, h.y, e.x, e.y, h.stats.range + 200))) continue;
    const d = dist2(h.x, h.y, e.x, e.y);
    if (d < bd) {
      bd = d;
      best = e;
    }
  }
  return best;
};

export const updateHeroes = (s: BattleState): void => {
  for (const h of s.heroes) {
    h.animT++;
    if (h.downTicks > 0) {
      // In the crypt nobody gets up alone: an ally has to come (see dungeon.ts).
      h.shove = 0;
      if (s.mode === 'dungeon') continue;
      h.downTicks--;
      if (h.downTicks === 0) respawnHero(s, h);
      continue;
    }
    if (h.invuln > 0) h.invuln--;
    if (h.undying && h.undying > 0) h.undying--;
    if (h.ward && h.ward > 0) h.ward--;
    if (h.shieldTicks && h.shieldTicks > 0 && --h.shieldTicks === 0) h.shield = 0;
    if (h.fear && h.fear > 0) h.fear--;
    if (h.unMark) unsleepingHeroTick(h);
    if (h.unSmoke && h.unSmoke > 0) h.unSmoke--;
    if (h.curse && h.curse > 0) h.curse--;
    if (h.inspire && h.inspire > 0) h.inspire--;
    if (h.roar && h.roar > 0) h.roar--;
    // Deep roots: nothing holds or throws it, and it mends every second.
    if (h.rooted && h.rooted > 0) {
      h.rooted--;
      h.stun = 0;
      h.shove = 0;
      if (h.rootWardBp) wardHero(h, h.rootWardBp, 3);
      if (h.rooted % 12 === 0 && h.rootBp) healHero(s, h, mulBp(h.stats.maxHp, h.rootBp), h.pid);
    }
    // A cleric's blessing: a share of its life back every second.
    if (h.bleedOn && h.bleedOn > 0) h.bleedOn--;
    if (h.intercede && h.intercede > 0) h.intercede--;
    if (h.hidden && h.hidden > 0) h.hidden--;
    if (h.choir && h.choir > 0) h.choir--;
    if (h.atone && h.atone > 0) h.atone--;
    if (h.blessed && h.blessed > 0) h.blessed--;
    if (h.sacrifice && h.sacrifice > 0) h.sacrifice--;
    if (h.wolf && h.wolf > 0) {
      h.wolf--;
      // The wolf's shape fades: the speed it had before.
      if (h.wolf === 0 && h.wolfBase) {
        h.stats.speed = h.wolfBase;
        h.wolfBase = undefined;
      }
    }
    if (h.eagle && h.eagle > 0) {
      h.eagle--;
      // The hawk eye fades: the reach it had before.
      if (h.eagle === 0 && h.eagleBase) {
        h.stats.range = h.eagleBase;
        h.eagleBase = undefined;
      }
    }
    if (h.areaBlow && h.areaBlow > 0) {
      h.areaBlow--;
      // The avatar wards the allies near it.
      if (h.avatarWardBp) for (const o of s.heroes) if (o.downTicks === 0 && withinRange(h.x, h.y, o.x, o.y, SKILL_FX.avatarOfLight.radius)) wardHero(o, h.avatarWardBp, 3);
    }
    if (h.renew && h.renew > 0) {
      h.renew--;
      if (h.renew % 12 === 0 && h.renewBp) healHero(s, h, mulBp(h.stats.maxHp, h.renewBp), h.pid);
    }
    if (h.frenzy && h.frenzy > 0) {
      h.frenzy--;
      // Unstoppable: nothing holds the barbarian while the frenzy lasts.
      h.stun = 0;
      h.fear = 0;
      if (h.sleep) h.sleep = 0;
    }
    if (h.bear && h.bear > 0 && h.bear - 1 === 0) shiftBear(s, h, 0);
    else if (h.bear && h.bear > 0) h.bear--;
    refreshStats(s, h);
    if (h.echo && s.tick >= h.echo.at) echoCast(s, h);
    uniqueHeroTick(s, h);
    gearHeroTick(s, h);
    kaelaSeed(s, h);
    if (h.spin && h.spin > 0 && s.tick % FORKS.whirlwind.every === 0) {
      h.spin--;
      whirlPulse(s, h, FORKS.whirlwind.damageBp);
    }
    if (h.taunt > 0) h.taunt--;
    for (let i = 0; i < 3; i++) if ((h.skillCd[i] as number) > 0) h.skillCd[i] = (h.skillCd[i] as number) - 1;
    if (h.overloadCd) h.overloadCd--;
    if (h.attackCd > 0) h.attackCd--;
    if (h.control === 'live') h.idleTicks++;
    // Poisoned: a little every second.
    if (h.poison && h.poison > 0) {
      h.poison--;
      if (h.poison % 12 === 0) damageHero(s, h, h.poisonDmg ?? 0, null, 'poison');
      if (h.downTicks > 0) continue;
    }
    if ((h.shove ?? 0) > 0) shoveTick(s, h);
    if (h.stun > 0) {
      h.stun--;
      continue;
    }
    if ((h.shove ?? 0) > 0) continue;
    // Asleep (sleep moths): an ally close by shakes it awake; otherwise it sleeps on.
    if (h.sleep && h.sleep > 0) {
      const r = NIGHTMARES.moth.sleeps?.wakeRadius ?? 0;
      if (s.heroes.some((o) => o.id !== h.id && o.downTicks === 0 && withinRange(o.x, o.y, h.x, h.y, r))) wake(s, h);
      else {
        if (--h.sleep === 0) h.wokeAt = s.tick;
        h.moving = false;
        continue;
      }
    }
    if (h.anim !== 'idle' && h.anim !== 'walk' && h.animT > 6) h.anim = 'idle';
    regen(s, h);
    root(s, h);
    act(s, h);
    purify(s, h);
    if (s.tick % 12 === 0) {
      const p = pstats(s, h.pid);
      const sec = s.map.sectors[h.post];
      // Only at the bastion: roaming an incursion or a saga field says nothing about how far a player strays from a gate.
      if (sec && h.control === 'live' && s.map.kind === 'bastion' && s.enemies.length > 0) {
        const g = hexToWorld(sec.gate.q, sec.gate.r);
        histAdd(p.leashHist, isqrt(dist2(h.x, h.y, g.x, g.y)), 500);
      }
    }
  }
};

/** Thrown back by an ogre: one tick of the slide, cut short by ground it can't stand on; at the end it takes up its walk from there. */
const shoveTick = (s: BattleState, h: Hero): void => {
  const x = h.x + (h.shoveX ?? 0);
  const y = h.y + (h.shoveY ?? 0);
  const on = heroWalkable(s, cellAtWorld(s, x, y));
  if (on) {
    h.x = x;
    h.y = y;
  }
  h.shove = on ? (h.shove ?? 1) - 1 : 0;
  if (h.shove > 0) return;
  ev(s, { t: 'boom', x: h.x, y: h.y, r: 400, kind: 'knock' });
  h.ax = h.x;
  h.ay = h.y;
  // The old path starts where it was hit, maybe across a wall from here.
  if (h.moving) orderMove(s, h, h.destX, h.destY);
  else {
    h.path = [];
    h.destX = h.x;
    h.destY = h.y;
  }
};

const regen = (s: BattleState, h: Hero): void => {
  if (h.hp >= h.stats.maxHp) return;
  if (s.tick - h.hurtTick < REGEN_DELAY) return;
  if (s.tick % 12 !== 0) return;
  h.hp += idiv(h.stats.maxHp, 100) + 1;
  if (h.hp > h.stats.maxHp) h.hp = h.stats.maxHp;
};

const act = (s: BattleState, h: Hero): void => {
  // Hands full (expeditions): it walks, it holds its blows.
  if (h.carry !== undefined) {
    h.target = 0;
    if (!h.moving) return;
    h.anim = 'walk';
    if (followPath(s, h)) {
      h.moving = false;
      h.anim = 'idle';
      h.ax = h.x;
      h.ay = h.y;
    }
    return;
  }
  // Explicit target: chase and hit.
  if (h.target > 0) {
    const e = findEnemy(s, h.target);
    if (!e || !targetable(s, e) || (isMelee(h) && overhead(e))) {
      h.target = 0;
      h.forced = false;
    } else {
      const reach = h.stats.range + (isMelee(h) ? 150 : 0);
      if (withinRange(h.x, h.y, e.x, e.y, reach)) {
        h.moving = false;
        h.path = [];
        // A shining colossus: the hero holds its blows (skills still bounce off it).
        if (!shining(s, e)) basicAttack(s, h, e);
        return;
      }
      if (h.forced || h.control !== 'live') {
        // Chase: straight line if the target is close, else re-path every half second.
        if (h.path.length === 0 || s.tick % 6 === 0) {
          const from = cellAtWorld(s, h.x, h.y);
          const to = cellAtWorld(s, e.x, e.y);
          h.path = findPath(s, from, to);
        }
        h.destX = e.x;
        h.destY = e.y;
        h.anim = 'walk';
        followPath(s, h);
        return;
      }
      h.target = 0;
    }
  }
  if (h.moving) {
    h.anim = 'walk';
    if (followPath(s, h)) {
      h.moving = false;
      h.anim = 'idle';
      h.ax = h.x;
      h.ay = h.y;
      // The player put the hero here: stay put a moment instead of charging back at whatever is near.
      if (h.control === 'live') h.calm = s.tick + LIVE_CALM;
    }
    return;
  }
  // Idle: hit whatever is in range; melee heroes step out to engage (a live one, up to three tiles from where it was left).
  const radius = isMelee(h) ? (h.control === 'live' ? liveReach(h) : MELEE_AUTO_CHASE) : h.stats.range;
  const e = autoTarget(s, h, radius);
  if (!e) {
    if (h.anim === 'walk') h.anim = 'idle';
    // Down a crypt a live hero stays where the last nightmare fell: walking back on its own between waves read
    // as being pulled. Its spot is where it stands now.
    if (s.mode === 'dungeon' && h.control === 'live') {
      h.ax = h.x;
      h.ay = h.y;
      return;
    }
    // Drift back to the anchor after an auto-chase (a live hero from afar goes around what stands between).
    if (!withinRange(h.x, h.y, h.ax, h.ay, 200)) {
      h.anim = 'walk';
      if (h.control === 'live' && !withinRange(h.x, h.y, h.ax, h.ay, 1500)) {
        if (h.path.length === 0 || s.tick % 6 === 0) h.path = findPath(s, cellAtWorld(s, h.x, h.y), cellAtWorld(s, h.ax, h.ay));
        h.destX = h.ax;
        h.destY = h.ay;
        followPath(s, h);
      } else stepToward(h, h.ax, h.ay, heroPx(h));
    }
    return;
  }
  if (withinRange(h.x, h.y, e.x, e.y, h.stats.range + (isMelee(h) ? 150 : 0))) {
    if (!shining(s, e)) basicAttack(s, h, e);
    return;
  }
  // The Old Uro: nobody walks up to it unordered (standing still is how it is hunted).
  if (e.kind === 'uro') return;
  // A live hero waits a moment after the player moved it, and leaves bosses alone past a short step; ghosts hold their posts.
  const chase = h.control === 'live' ? (bigFoe(e) ? LIVE_CHASE.boss : liveReach(h)) : MELEE_AUTO_CHASE + 300;
  // The bear doesn't wait: turning into one is already the order to fight.
  if (h.control === 'live' && s.tick < (h.calm ?? 0) && (h.bear ?? 0) === 0) return;
  if (isMelee(h) && withinRange(h.ax, h.ay, e.x, e.y, chase)) {
    h.anim = 'walk';
    const next = { x: e.x, y: e.y };
    const cell = cellAtWorld(s, h.x, h.y);
    const nextCell = cellAtWorld(s, next.x, next.y);
    // Farther than a tile, a live hero goes around what stands between (re-pathing every half second).
    if (h.control === 'live' && !withinRange(h.x, h.y, e.x, e.y, 1500)) {
      if (h.path.length === 0 || s.tick % 6 === 0) {
        h.path = heroRoute(s, cell, nextCell);
        while (h.path.length > 0 && !heroWalkable(s, h.path[h.path.length - 1] as number)) h.path.pop();
      }
      h.destX = e.x;
      h.destY = e.y;
      followPath(s, h);
      return;
    }
    if (cell === nextCell || heroWalkable(s, nextCell)) stepToward(h, next.x, next.y, heroPx(h));
  }
};

/** How far a live melee hero steps out from where it was left; the bear goes as far as the druid was shooting. */
const liveReach = (h: Hero): number => ((h.bear ?? 0) > 0 && (h.base?.range ?? 0) > LIVE_CHASE.reach ? (h.base?.range as number) : LIVE_CHASE.reach);

/** Bosses, warlords and elites: a live melee hero only steps out a little toward one. */

const purify = (s: BattleState, h: Hero): void => {
  if (h.moving) {
    h.purifying = 0;
    return;
  }
  for (const st of s.structures) {
    if (!st.cursed || st.destroyed) continue;
    const c = hexToWorld(st.q, st.r);
    if (!withinRange(h.x, h.y, c.x, c.y, PURIFY_RANGE)) continue;
    if (h.purifying !== st.id) {
      h.purifying = st.id;
      st.purify = 0;
    }
    st.purify++;
    if (st.purify >= PURIFY_TICKS) {
      st.cursed = false;
      st.purify = 0;
      h.purifying = 0;
      ev(s, { t: 'struct', id: st.id, what: 'purified' });
    }
    return;
  }
  h.purifying = 0;
};

/** Ash amulet: heal the hero that landed the killing blow; a bloodlust frenzy draws out. */
export const onKill = (s: BattleState, pid: string, e?: Enemy): void => {
  bloodlust(s, pid);
  for (const h of s.heroes) {
    if (h.pid !== pid || h.downTicks > 0) continue;
    if (h.relics.includes('ashAmulet')) {
      h.hp += mulBp(awake(h, 'ashAmulet') ? RELIC_AWAKENED_FX.ashAmulet.healPerKill : RELIC_FX.ashAmulet.healPerKill, skillLevelBp(h.level));
      if (h.hp > h.stats.maxHp) h.hp = h.stats.maxHp;
    }
    gearOnKill(s, h, e);
  }
};
